import React, { useState, useEffect, useContext } from 'react';
import { Button, MenuItem, FormControl, InputLabel, Select, TextField, Grid, Typography, useMediaQuery, IconButton, Menu, ListItemIcon } from '@mui/material';
import { UserContext } from '../../../context/user_context/UserContext';
import { Loading } from '../../../components/loading/Loading';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { assetsSnapshotListener } from '../../../repository/AssetDAO';
import AssetTable from './AssetTable';
import AddAssetModal from './modals/AddAssetModal';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { TablePaginationKeys } from '../../../components/pagination/TablePaginationKeys';
import { handleChangePage, handleChangeRowsPerPage } from '../../../components/pagination/PaginationHelpers';
import { Add, FilterAlt, MoreVert, QrCode, Close } from '@mui/icons-material';
import FilterDialog from '../../../components/filter_dialog/FilterDialog';
import { useQRCodePrinter } from '../../../components/qr_code/QRCodePrinter';
import { handleMenuClick, handleMenuClose } from '../../../components/utils/MenuHandlers';

function AssetManagement() {
    const { firebaseUser } = useContext(UserContext);
    const [assetsData, setAssetsData] = useState();
    const [filteredAssets, setFilteredAssets] = useState([]);
    const [showAddAssetModal, setShowAddAssetModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const isMobile = useMediaQuery('(max-width:600px)');
    const { hasPermission } = usePermissions();
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const { printMultipleQRCodes } = useQRCodePrinter();

    useEffect(() => {
        const savedRowsPerPage = localStorage.getItem(TablePaginationKeys.AssetRowsPerPage);
        if (savedRowsPerPage) {
            setRowsPerPage(parseInt(savedRowsPerPage, 10));
        }
    }, []);

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribe = assetsSnapshotListener(firebaseUser.organization, setAssetsData);
            return () => unsubscribe();
        }
    }, [firebaseUser]);

    useEffect(() => {
        if (assetsData) filterAndSortAssets();
    }, [assetsData, searchTerm, sortOrder]);

    const filterAndSortAssets = () => {
        let filtered = [...assetsData];

        if (searchTerm) {
            filtered = filtered.filter(asset => {
                const name = asset.name ? asset.name.toLowerCase() : '';
                const serialNumber = asset.serial_number ? asset.serial_number.toLowerCase() : ''; // Ensure you use asset.serial_number
                return name.includes(searchTerm.toLowerCase()) || serialNumber.includes(searchTerm.toLowerCase());
            });
        }

        filtered.sort((a, b) => {
            const nameA = a.name ? a.name.toLowerCase() : '';
            const nameB = b.name ? b.name.toLowerCase() : '';

            if (sortOrder === 'asc') {
                return nameA.localeCompare(nameB);
            } else if (sortOrder === 'desc') {
                return nameB.localeCompare(nameA);
            } else {
                return 0;
            }
        });

        setFilteredAssets(filtered);
    };


    const handleOpenBottomSheet = () => {
        setIsBottomSheetOpen(true);
    };

    const handleCloseBottomSheet = () => {
        setIsBottomSheetOpen(false);
    };

    const handleSelectionChange = (selectedIds) => {
        setSelectedItems(selectedIds);
    };

    const handleExitSelectionMode = () => {
        setIsSelectionMode(false);
        setSelectedItems([]);
    };

    const handlePrintSelectedQRCodes = () => {
        const selectedItemsData = filteredAssets.filter(item => selectedItems.includes(item.id));
        printMultipleQRCodes(selectedItemsData, 'assets');
        setIsSelectionMode(false);
        setSelectedItems([]);
    };

    return (
        <>
            {assetsData ? (
                <AppSidebar
                    content={
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12}>
                                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                    {!isMobile && (
                                        <Grid item>
                                            <Typography variant="h5">{t(StringKeys.ASSETS)}</Typography>
                                        </Grid>
                                    )}
                                    <Grid item>
                                        <Grid container spacing={2} alignItems="center">
                                            {!isMobile && (
                                                <>
                                                    <Grid item>
                                                        <TextField
                                                            label={t(StringKeys.SEARCH)}
                                                            variant="outlined"
                                                            size="small"
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                        />
                                                    </Grid>                                                                                       
                                                    <Grid item>
                                                        <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                                                            <InputLabel>{t(StringKeys.SORT)}</InputLabel>
                                                            <Select
                                                                value={sortOrder}
                                                                onChange={(e) => setSortOrder(e.target.value)}
                                                                label={t(StringKeys.SORT)}
                                                            >
                                                                <MenuItem value="asc">A-Z</MenuItem>
                                                                <MenuItem value="desc">Z-A</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    {!isSelectionMode ? (
                                                        <>
                                                            <Grid item>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={() => setIsSelectionMode(true)}
                                                                    startIcon={<QrCode />}
                                                                >
                                                                    {t(StringKeys.PRINT_QR_CODES)}
                                                                </Button>
                                                            </Grid>
                                                            {hasPermission(Modules.ASSETS, PermissionTypes.WRITE) && (
                                                                <Grid item>
                                                                    <Button
                                                                        variant="contained"
                                                                        onClick={() => setShowAddAssetModal(true)}
                                                                        startIcon={<Add />}
                                                                    >
                                                                        {t(StringKeys.ADD_ASSET)}
                                                                    </Button>
                                                                </Grid>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Grid item>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={handleExitSelectionMode}
                                                                >
                                                                    {t(StringKeys.CANCEL)}
                                                                </Button>
                                                            </Grid>
                                                            <Grid item>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={handlePrintSelectedQRCodes}
                                                                    startIcon={<QrCode />}
                                                                    disabled={selectedItems.length === 0}
                                                                >
                                                                    {selectedItems.length > 0 
                                                                        ? t(StringKeys.PRINT_X_SELECTED_QR_CODES, { count: selectedItems.length })
                                                                        : t(StringKeys.SELECT_ITEMS_TO_PRINT)
                                                                    }
                                                                </Button>
                                                            </Grid>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {isMobile && (
                                    <>
                                        <Typography variant="h6">{t(StringKeys.ASSETS)}</Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={10}>
                                                <TextField
                                                    label={t(StringKeys.SEARCH)}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)} />
                                            </Grid>
                                            <Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                                    <MoreVert />
                                                </IconButton>
                                                <Menu
                                                    anchorEl={anchorEl}
                                                    open={Boolean(anchorEl)}
                                                    onClose={() => handleMenuClose(setAnchorEl)}
                                                >
                                                    {!isSelectionMode ? [
                                                        <MenuItem
                                                            key="filter"
                                                            onClick={() => {
                                                                handleOpenBottomSheet();
                                                                handleMenuClose(setAnchorEl);
                                                            }}
                                                        >
                                                            <ListItemIcon>
                                                                <FilterAlt />
                                                            </ListItemIcon>
                                                            {t(StringKeys.FILTER)}
                                                        </MenuItem>,
                                                        <MenuItem 
                                                            key="add"
                                                            onClick={() => { setShowAddAssetModal(true); handleMenuClose(setAnchorEl); }}
                                                        >
                                                            <ListItemIcon>
                                                                <Add />
                                                            </ListItemIcon>
                                                            {t(StringKeys.ADD_ASSET)}
                                                        </MenuItem>
                                                    ] : [
                                                        <MenuItem 
                                                            key="cancel"
                                                            onClick={() => { handleExitSelectionMode(); handleMenuClose(setAnchorEl); }}
                                                        >
                                                            <ListItemIcon>
                                                                <Close />
                                                            </ListItemIcon>
                                                            {t(StringKeys.CANCEL)}
                                                        </MenuItem>
                                                    ]}
                                                </Menu>
                                            </Grid>
                                        </Grid>
                                    </>
                                )}                                       
                            </Grid>

                            <Grid item xs={12}>
                                <AssetTable 
                                    assets={filteredAssets} 
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    handleChangePage={(event, newPage) => handleChangePage(event, newPage, setPage)}
                                    handleChangeRowsPerPage={(event) => handleChangeRowsPerPage(event, TablePaginationKeys.AssetRowsPerPage, setPage, setRowsPerPage)}
                                    onSelectionChange={handleSelectionChange}
                                    isSelectionMode={isSelectionMode}
                                />
                            </Grid>
                        </Grid>
                    }
                />
            ) : (
                <AppSidebar content={<Loading />} />
            )}
            <AddAssetModal
                show={showAddAssetModal}
                onHide={() => setShowAddAssetModal(false)}
            />

            <FilterDialog
                open={isBottomSheetOpen}
                onClose={handleCloseBottomSheet}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                filters={[
                ]}
            />
        </>
    );
}

export default AssetManagement;
